import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell.js";
import { defaultBrandKit, type BrandKit } from "@veyso/core";
import { BrandKitPanel } from "../settings/BrandKit.js";
import { BrandWebsite } from "../settings/BrandWebsite.js";
import { BillingPanel } from "../settings/BillingPanel.js";
import { TeamPanel } from "../settings/TeamPanel.js";
import { api, type SettingsView } from "../api.js";
import { capture } from "../lib/posthog.js";

/**
 * What this page is for.
 *
 * Provider keys and models are not here any more. They are read from the
 * environment — `ANTHROPIC_API_KEY`, `SCRIPT_MODEL` and the rest — which a
 * deployment already has to set for the worker to run at all. A form that could
 * disagree with the machine it runs on is a second place to look when a
 * generation fails, and the first place people look is the wrong one.
 *
 * What remains is what belongs to the workspace rather than to the deployment:
 * how videos should look and sound, and what there is to spend.
 *
 * The recorder had a section here for pairing a browser extension that is not
 * published, so it was a page of controls for a thing nobody could install.
 * When the extension ships it will need pairing again, and the endpoints it
 * pairs against are still there — see `/api/recorder/*`.
 */
/**
 * Which section a hash names, and the address that names it.
 *
 * Exported and pure so the pair can be checked without a DOM — the bug they
 * exist for was a mismatch between what the URL said and what the page
 * believed, which is exactly the thing a round trip catches.
 */
export function sectionForHash(hash: string): SettingsSection {
  const wanted = hash.replace("#", "");
  return SECTIONS.some((entry) => entry.id === wanted) ? (wanted as SettingsSection) : "brand";
}

/** The default section owns the bare path, so `/settings` is not `/settings#brand`. */
export function pathForSection(section: SettingsSection): string {
  return section === "brand" ? "/settings" : `/settings#${section}`;
}

export const SECTIONS = [
  { id: "brand" as const, label: "Brand kit" },
  { id: "team" as const, label: "Team" },
  { id: "billing" as const, label: "Billing" },
];

export type SettingsSection = (typeof SECTIONS)[number]["id"];

export const Settings: React.FC = () => {
  /**
   * Which section, read from the router rather than from `window.location`.
   *
   * Held in the hash so the brand kit can be linked to — anything wanting to
   * send somebody to their colours could otherwise only send them to the page
   * and hope.
   *
   * Derived, not stored. It used to be state seeded from `window.location.hash`
   * and kept up to date by a `hashchange` listener, and both halves were wrong
   * in a way that only showed up on this page:
   *
   * `hashchange` does not fire for a `<Link>`. React Router navigates with
   * `history.pushState`, which fires neither `hashchange` nor `popstate` — so
   * following the header's "Add credit" from anywhere *else* worked, because
   * this page mounted fresh and read the hash once, and following it from
   * Brand kit or Team did nothing at all.
   *
   * And switching tabs called `window.history.replaceState` directly, behind
   * the router's back. The URL changed and the router's own `location` did
   * not — so after one tab click the chip still believed it was on `#billing`,
   * and preventDefault'ed the very click that would have got you there.
   *
   * One source of truth fixes both: the router's location decides the section,
   * and the tabs navigate rather than rewrite the address bar.
   */
  const location = useLocation();
  const navigate = useNavigate();

  const section = useMemo<SettingsSection>(() => sectionForHash(location.hash), [location.hash]);

  // `replace` rather than a push: switching tabs is not a place in history
  // somebody wants the back button to walk through.
  const setSection = (next: SettingsSection) => navigate(pathForSection(next), { replace: true });

  const [brandKit, setBrandKit] = useState<BrandKit>(defaultBrandKit);
  const [settings, setSettings] = useState<SettingsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api
      .settings()
      .then((current) => {
        setSettings(current);
        setBrandKit(current.brandKit);
      })
      .catch((cause: Error) => setError(cause.message));
  }, []);

  const dirty = useMemo(() => {
    if (!settings) return false;
    // Compared whole, because that is how it is edited and saved. A palette has
    // no field worth diffing on its own.
    return JSON.stringify(brandKit) !== JSON.stringify(settings.brandKit);
  }, [settings, brandKit]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      setSettings(await api.saveSettings({ brandKit }));
      capture("brand_kit_saved");
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2600);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell
      breadcrumb={
        <>
          <span className="text-ink-400">/</span>
          <Link to="/dashboard" className="hover:text-ink-100">
            Projects
          </Link>
          <span className="text-ink-400">/</span>
          <span className="font-medium text-ink-100">Settings</span>
        </>
      }
    >
      <div className="mx-auto flex w-full max-w-[860px] gap-7 pb-8">
        {/* A side menu rather than one long page: a palette and a pairing token
            are read at different times by different people. */}
        <nav className="sticky top-4 flex w-[168px] shrink-0 flex-col gap-0.5 self-start">
          {SECTIONS.map((entry) => (
            <button
              key={entry.id}
              onClick={() => setSection(entry.id)}
              data-selected={section === entry.id}
              className="rounded-[6px] px-2.5 py-2 text-left text-[13px] text-ink-300 transition-colors hover:bg-bay-850 hover:text-ink-100 data-[selected=true]:bg-bay-800 data-[selected=true]:text-ink-100"
            >
              {entry.label}
            </button>
          ))}
        </nav>

        <div className="flex min-w-0 flex-1 flex-col gap-5">
          {error ? <p className="text-[12px] text-signal-bad">{error}</p> : null}

          {/* Billing has no Save button: adding credit takes effect when Stripe
              says it did, and money is the wrong thing to hold in a form
              somebody can lose by navigating away. */}
          {section === "team" ? (
            // Like billing, no Save button: an invitation is sent the moment
            // it is sent, and there is nothing to hold in a form.
            <TeamPanel />
          ) : section === "billing" ? (
            <BillingPanel />
          ) : (
            <>
              {/* Above the kit, because it is the thing that fills the kit in.
                  Underneath it read as an afterthought — somebody scrolled past
                  every colour, set them by hand, and then met the control that
                  would have done it. Top-down is also the order onboarding
                  asks in, so the page matches how the kit was made. */}
              <BrandWebsite kit={brandKit} onChange={setBrandKit} />
              <BrandKitPanel kit={brandKit} onChange={setBrandKit} />
              <div className="flex items-center justify-end gap-3">
                {saved ? <span className="text-[12px] text-signal-ok">Saved</span> : null}
                <button className="btn btn-primary" disabled={!dirty || busy} onClick={() => void save()}>
                  {busy ? "Saving…" : "Save settings"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
};
